import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  GitBranch,
  Globe,
  Loader2,
  Lock,
  Network as NetworkIcon,
  Search,
} from 'lucide-react'
import { OperatorPage, SectionPanel } from '@/components/operator-surfaces'
import { useAuth } from '@/contexts/AuthContext'
import { WorkspaceAsideCard } from '@/components/workspace-form'
import { Button } from '@/components/ui/button'
import { FormMessage } from '@/components/ui/form-message'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { apiClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import type { NetworkSourceType } from '@/types/network'

interface GitHubRepository {
  id: number
  name: string
  full_name: string
  description: string
  private: boolean
  clone_url: string
  html_url: string
  language: string
  updated_at: string
  default_branch: string
  owner: {
    login: string
  }
}

type SourceType = 'empty' | 'github'

const sourceOptions: Array<{
  id: SourceType
  title: string
  description: string
  icon: typeof GitBranch
}> = [
  {
    id: 'github',
    title: 'GitHub repository',
    description: 'Create the workspace from an existing repository and carry its default branch forward.',
    icon: GitBranch,
  },
  {
    id: 'empty',
    title: 'Manual workspace',
    description: 'Start with a name only and upload configuration later through the CLI flow.',
    icon: NetworkIcon,
  },
]

export default function AddNetworkPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { toast } = useToast()
  const [submitting, setSubmitting] = useState(false)
  const [sourceType, setSourceType] = useState<SourceType>('github')
  const [networkName, setNetworkName] = useState('')
  const [networkDescription, setNetworkDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [repositories, setRepositories] = useState<GitHubRepository[]>([])
  const [filteredRepos, setFilteredRepos] = useState<GitHubRepository[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepository | null>(null)
  const [needsGitHubAuth, setNeedsGitHubAuth] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formErrors, setFormErrors] = useState<{
    networkName?: string
    repository?: string
  }>({})

  useEffect(() => {
    if (sourceType === 'github') {
      fetchGitHubRepositories()
    }
  }, [sourceType])

  useEffect(() => {
    if (sourceType === 'github' && selectedRepo) {
      setNetworkName(selectedRepo.full_name)
      setNetworkDescription(selectedRepo.description || '')
      setFormErrors((current) => ({ ...current, repository: undefined }))
    }
  }, [sourceType, selectedRepo])

  useEffect(() => {
    if (searchTerm) {
      const filtered = repositories.filter((repo) =>
        repo.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (repo.description && repo.description.toLowerCase().includes(searchTerm.toLowerCase())),
      )
      setFilteredRepos(filtered)
      return
    }

    setFilteredRepos(repositories)
  }, [searchTerm, repositories])

  const fetchGitHubRepositories = async () => {
    setError(null)

    try {
      setLoading(true)
      // Server uses GitHub App installation tokens — no user token needed
      const response = await apiClient.getRepositories()

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch repositories')
      }

      const repos = response.data.repositories as GitHubRepository[]
      setRepositories(repos)
      setFilteredRepos(repos)
      // If empty, the GitHub App likely isn't installed on any org/account
      setNeedsGitHubAuth(repos.length === 0)
    } catch (err) {
      console.error('Error fetching repositories:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch repositories')
    } finally {
      setLoading(false)
    }
  }

  const handleInstallGitHubApp = () => {
    // Redirect to GitHub App installation page — user returns here after installing
    localStorage.setItem('auth_return_url', '/networks/add')
    window.location.href = apiClient.getGitHubAppInstallUrl()
  }

  const handleCreateNetwork = async () => {
    const nextErrors: typeof formErrors = {}

    if (sourceType === 'empty' && !networkName.trim()) {
      nextErrors.networkName = 'Enter a network name before creating this workspace.'
    }

    if (sourceType === 'github' && !selectedRepo) {
      nextErrors.repository = 'Choose a GitHub repository to continue.'
    }

    if (nextErrors.networkName || nextErrors.repository) {
      setFormErrors(nextErrors)
      return
    }

    setSubmitting(true)
    try {
      let sourceConfig: Record<string, unknown>
      let sourceTypeValue: NetworkSourceType
      let defaultBranch: string | undefined

      if (sourceType === 'github' && selectedRepo) {
        sourceTypeValue = 'github'
        sourceConfig = {
          githubRepoId: selectedRepo.id,
          owner: selectedRepo.owner?.login || selectedRepo.full_name.split('/')[0],
          name: selectedRepo.name,
          fullName: selectedRepo.full_name,
          cloneUrl: selectedRepo.clone_url,
          private: selectedRepo.private,
          defaultBranch: selectedRepo.default_branch,
          description: selectedRepo.description,
        }
        defaultBranch = selectedRepo.default_branch || 'main'
      } else {
        sourceTypeValue = 'cli'
        sourceConfig = {
          uploadedBy: 'web-ui',
          configCount: 0,
        }
      }

      const response = await apiClient.createNetwork({
        name: networkName.trim(),
        description: networkDescription.trim(),
        sourceType: sourceTypeValue,
        sourceConfig,
        defaultBranch,
      })

      if (response.success && response.data) {
        toast({
          title: 'Network created',
          description: `Workspace "${response.data.network.name}" is ready.`,
          variant: 'success',
        })
        navigate(`/networks/${response.data.network.id}`)
      } else {
        throw new Error(response.error || 'Failed to create network')
      }
    } catch (creationError) {
      toast({
        title: 'Create failed',
        description: creationError instanceof Error ? creationError.message : 'Failed to create network',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const resetForManualSource = () => {
    setSourceType('empty')
    setSelectedRepo(null)
    setNetworkName('')
    setNetworkDescription('')
    setFormErrors({})
    setError(null)
    setSearchTerm('')
  }

  if (!isAuthenticated) {
    navigate('/login?redirect=%2Fnetworks%2Fadd')
    return null
  }

  return (
    <OperatorPage
      eyebrow="Create"
      title="Create a network workspace"
      description="Use a GitHub repository or a manual placeholder, then confirm the exact workspace you are creating. This route stays narrow on purpose so the form reads like an operational task, not a dashboard."
      actions={(
        <Button variant="ghost" onClick={() => navigate('/networks')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Networks
        </Button>
      )}
      hero={(
        <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <WorkspaceAsideCard
            title="Workflow"
            description="Choose one source, confirm the details, then create the workspace. Empty, success, and error states use the same inline treatment as the rest of the authenticated UI."
          />
          <WorkspaceAsideCard
            title="Current selection"
            tone={selectedRepo || sourceType === 'empty' ? 'accent' : 'default'}
            description={
              sourceType === 'github'
                ? selectedRepo
                  ? `${selectedRepo.full_name} on ${selectedRepo.default_branch || 'main'}`
                  : 'No repository selected yet.'
                : networkName.trim()
                  ? `Manual workspace: ${networkName.trim()}`
                  : 'Manual workspace details have not been entered yet.'
            }
          >
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Source</dt>
                <dd className="font-medium text-foreground">
                  {sourceType === 'github' ? 'GitHub' : 'Manual'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Name</dt>
                <dd className="truncate font-medium text-foreground">
                  {networkName.trim() || 'Pending'}
                </dd>
              </div>
            </dl>
          </WorkspaceAsideCard>
        </section>
      )}
    >
      <div className="space-y-6">
        <SectionPanel
          eyebrow="Step 1"
          title="Choose a source"
          description="Pick the one starting point for this workspace. Non-supported sources stay secondary until they are ready."
        >
            <div className="grid gap-4 md:grid-cols-2">
              {sourceOptions.map((option) => {
                const Icon = option.icon
                const active = sourceType === option.id

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      if (option.id === 'empty') {
                        resetForManualSource()
                        return
                      }

                      setSourceType('github')
                      setFormErrors((current) => ({ ...current, repository: undefined }))
                    }}
                    className={cn(
                      'rounded-[20px] border px-5 py-5 text-left transition-colors',
                      active
                        ? 'border-primary/30 bg-primary/[0.06]'
                        : 'border-border/70 bg-background/70 hover:border-border hover:bg-muted/30',
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-3">
                        <span
                          className={cn(
                            'flex h-11 w-11 items-center justify-center rounded-2xl border',
                            active
                              ? 'border-primary/20 bg-primary/10 text-primary'
                              : 'border-border/70 bg-muted/40 text-muted-foreground',
                          )}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <div className="space-y-1">
                          <p className="font-medium text-foreground">{option.title}</p>
                          <p className="text-sm leading-6 text-muted-foreground">{option.description}</p>
                        </div>
                      </div>
                      {active ? <Check className="mt-1 h-5 w-5 text-primary" /> : null}
                    </div>
                  </button>
                )
              })}
            </div>

            <WorkspaceAsideCard
              title="Coming later"
              description="GitLab, HTTP, and additional managed source types remain intentionally absent here until the create flow for each can be unambiguous."
            />
        </SectionPanel>

        {sourceType === 'empty' ? (
          <SectionPanel
            eyebrow="Step 2"
            title="Name the workspace"
            description="Use a stable, operator-readable name. The description is optional and only needs enough detail to help the next person understand the scope."
          >
              <div className="grid gap-5">
                <div className="space-y-2">
                  <Label htmlFor="network-name">Network name</Label>
                  <Input
                    id="network-name"
                    placeholder="Production datacenter"
                    value={networkName}
                    onChange={(event) => {
                      setNetworkName(event.target.value)
                      if (formErrors.networkName) {
                        setFormErrors((current) => ({ ...current, networkName: undefined }))
                      }
                    }}
                    aria-invalid={Boolean(formErrors.networkName)}
                    autoFocus
                  />
                  {formErrors.networkName ? (
                    <FormMessage variant="error">{formErrors.networkName}</FormMessage>
                  ) : (
                    <FormMessage>Choose the name operators will use when they run validations and review history.</FormMessage>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="network-description">Description</Label>
                  <Textarea
                    id="network-description"
                    placeholder="Optional context about the environment, ownership, or intended workflow."
                    value={networkDescription}
                    onChange={(event) => setNetworkDescription(event.target.value)}
                    rows={4}
                  />
                  <FormMessage>Descriptions are optional and should stay short. Raw technical detail belongs in configs and validation outputs instead.</FormMessage>
                </div>

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                  <Button variant="outline" onClick={() => navigate('/networks')}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateNetwork} disabled={submitting}>
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Create workspace
                  </Button>
                </div>
              </div>
          </SectionPanel>
        ) : (
          <>
            <SectionPanel
              eyebrow="Step 2"
              title="Select a repository"
              description="Search once, pick once, and then confirm the workspace name that will be created from that repository."
            >
                {needsGitHubAuth && !loading ? (
                  <WorkspaceAsideCard
                    title="Install the EtherPilot GitHub App"
                    description="No repositories found. Install the EtherPilot GitHub App on your organization or account to grant access to your repositories."
                  >
                    <Button onClick={handleInstallGitHubApp}>
                      <GitBranch className="mr-2 h-4 w-4" />
                      Install GitHub App
                    </Button>
                  </WorkspaceAsideCard>
                ) : error && !needsGitHubAuth ? (
                  <WorkspaceAsideCard
                    title="Repository list unavailable"
                    description={error}
                    tone="danger"
                  >
                    <Button
                      variant="outline"
                      onClick={() => {
                        setError(null)
                        fetchGitHubRepositories()
                      }}
                    >
                      Try again
                    </Button>
                  </WorkspaceAsideCard>
                ) : loading ? (
                  <WorkspaceAsideCard
                    title="Loading repositories"
                    description="Fetching repositories from GitHub now."
                  >
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      Working through your available repositories.
                    </div>
                  </WorkspaceAsideCard>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="repository-search">Repository search</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="repository-search"
                          type="text"
                          placeholder="Search by repository name or description"
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                          className="pl-9"
                        />
                      </div>
                      {formErrors.repository ? (
                        <FormMessage variant="error">{formErrors.repository}</FormMessage>
                      ) : (
                        <FormMessage>Choose the repository that should define this workspace.</FormMessage>
                      )}
                    </div>

                    {selectedRepo ? (
                      <WorkspaceAsideCard
                        title="Selected repository"
                        description={`${selectedRepo.full_name} • default branch ${selectedRepo.default_branch || 'main'}`}
                        tone="accent"
                      />
                    ) : null}

                    <div className="space-y-3">
                      {filteredRepos.length === 0 ? (
                        <WorkspaceAsideCard
                          title="No repositories matched"
                          description={
                            searchTerm
                              ? `No repositories matched "${searchTerm}". Clear the search or try another term.`
                              : 'No repositories are available for this account yet.'
                          }
                        />
                      ) : (
                        filteredRepos.map((repo) => {
                          const isSelected = selectedRepo?.id === repo.id

                          return (
                            <button
                              key={repo.id}
                              type="button"
                              onClick={() => {
                                setSelectedRepo(repo)
                                if (formErrors.repository) {
                                  setFormErrors((current) => ({ ...current, repository: undefined }))
                                }
                              }}
                              className={cn(
                                'w-full rounded-[20px] border px-4 py-4 text-left transition-colors',
                                isSelected
                                  ? 'border-primary/30 bg-primary/[0.06]'
                                  : 'border-border/70 bg-background/60 hover:bg-muted/30',
                              )}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 space-y-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="truncate font-medium text-foreground">{repo.full_name}</span>
                                    {repo.private ? (
                                      <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    ) : (
                                      <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    )}
                                  </div>
                                  {repo.description ? (
                                    <p className="text-sm leading-6 text-muted-foreground">{repo.description}</p>
                                  ) : null}
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    <span>Default branch: {repo.default_branch || 'main'}</span>
                                    {repo.language ? <span>Primary language: {repo.language}</span> : null}
                                    <span>Updated {new Date(repo.updated_at).toLocaleDateString()}</span>
                                  </div>
                                </div>
                                {isSelected ? <Check className="mt-1 h-5 w-5 shrink-0 text-primary" /> : null}
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                  </div>
                )}
            </SectionPanel>

            <SectionPanel
              eyebrow="Step 3"
              title="Review and create"
              description="The workspace name is derived directly from the repository so there is no second naming decision to reconcile later."
            >
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="network-name-preview">Workspace name</Label>
                    <Input
                      id="network-name-preview"
                      value={networkName}
                      disabled
                      className="bg-muted/40"
                    />
                    <FormMessage>The repository full name becomes the workspace name for the initial create flow.</FormMessage>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="network-description-preview">Repository description</Label>
                    <Textarea
                      id="network-description-preview"
                      value={networkDescription}
                      disabled
                      rows={4}
                      className="bg-muted/40"
                    />
                    <FormMessage>Repository metadata is carried into the initial description and can be adjusted later in network settings.</FormMessage>
                  </div>

                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                    <Button variant="outline" onClick={() => navigate('/networks')}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateNetwork} disabled={submitting || !selectedRepo}>
                      {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Create workspace
                    </Button>
                  </div>
                </div>
            </SectionPanel>
          </>
        )}
      </div>
    </OperatorPage>
  )
}
